import React, { ReactElement, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { useForm } from 'react-hook-form';
import { initialLoginFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema } from 'pages/LoginPage/schemaValidation';
import { EButtonVariants } from 'constants/Buttons';
import { RouterLink } from 'components/atoms/RouterLink';
import { useAuth } from 'models/authContext';
import { IAuthBodyRequest, IAuthResponse, ILoginForm } from 'models/auth';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import Logo from 'components/atoms/Logo';
import LogoImage from 'assets/images/Logo.svg';
import { ERouteLinks } from 'models/route';
import { Box } from '@mui/system';
import { Alert } from 'components/atoms/Alert';
import { AuthTemplate } from 'components/templates/AuthTemplate';
import { IData } from 'pages/AuthPage';
import { useNavigate } from 'react-router-dom';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { colors } from 'theme/colors';

export const FC_SYSTEM_DEVICE_ID = 'FC_SYSTEM_DEVICE_ID';

export interface ILoginPageProps {
  changePage: (p: number) => void;
  changeDataCallback: (p: IData) => void;
}

const LoginPage = ({ changePage, changeDataCallback }: ILoginPageProps): ReactElement => {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: initialLoginFormValues, resolver: yupResolver(schema) });
  const { logIn, authMessage, isLoginLoading, clearAuthErrors } = useAuth();
  const { authAlert, clearAlert } = useAlert();
  useEffect(
    () => () => {
      clearAuthErrors();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_AUTH_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onSubmit = (data: ILoginForm) => {
    const body: IAuthBodyRequest = {
      ...data,
      deviceToken: localStorage.getItem(FC_SYSTEM_DEVICE_ID) || null,
    };
    logIn(body, (res: IAuthResponse) => {
      if (res.accessToken) {
        navigate(ERouteLinks.Licences);
        changePage(3);
      } else if (res.phoneNumber) {
        changePage(2);
        const phoneNumber = res.phoneNumber.toString();
        const numberEndPart = phoneNumber.slice(phoneNumber.length - 3);
        changeDataCallback({
          ...data,
          phoneNumber: `*********${numberEndPart}`,
        });
      }
    });
  };
  return (
    <AuthTemplate>
      <Grid
        container
        size={{
          sm: 9,
          md: 6,
          lg: 5,
          xl: 6,
        }}
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          px: 2,
          pb: 5,
        }}
      >
        <Grid
          container
          size={{
            xl: 6,
          }}
          sx={{
            justifyContent: 'center',
          }}
        >
          <Grid
            container
            sx={{
              justifyContent: 'center',
              width: { xs: '120px', sm: '200px', xl: '220px' },
            }}
          >
            <Logo src={LogoImage} alt="Logo" />
          </Grid>
          <Grid
            size={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="button"
              sx={{
                textTransform: 'uppercase',
                my: 1,
                color: colors.primary500,
              }}
            >
              Manager
            </Typography>
          </Grid>
          <Grid size={12}>
            {authAlert ? (
              <Box
                sx={{
                  mb: 2,
                }}
              >
                <Alert text={authAlert.text} variant={authAlert.variant} />
              </Box>
            ) : (
              <Box
                sx={{
                  my: 1,
                  visibility: authMessage.text && !authAlert ? 'visible' : 'hidden',
                }}
              >
                <Alert text={authMessage.text} variant={authMessage.variant} />
              </Box>
            )}
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                mb: 2,
              }}
            >
              Log in to access FireCycle Manager
              <br />
              using your email address and password.
            </Typography>
          </Grid>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <Grid
              container
              sx={{
                justifyContent: 'center',
              }}
            >
              <Grid size={12}>
                <FormInput
                  name="email"
                  label="Email"
                  placeholder="name@domain.com"
                  control={control}
                  errors={errors}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: 32, sm: 40 },
                    },
                  }}
                />
                <FormInput
                  name="password"
                  type={EFormInputTypes.password}
                  label="Password"
                  placeholder="Password"
                  control={control}
                  errors={errors}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: 32, sm: 40 },
                    },
                  }}
                />
              </Grid>
              <Grid
                container
                size={{
                  xs: 12,
                  sm: 7,
                  xl: 12,
                }}
                sx={{
                  justifyContent: 'center',
                }}
              >
                <Grid
                  size={12}
                  sx={{
                    mb: 1,
                    mt: 2,
                  }}
                >
                  <Button
                    type="submit"
                    label="Login"
                    variant={EButtonVariants.contained}
                    isLoading={isLoginLoading}
                    fullWidth
                  />
                </Grid>
                <RouterLink to={ERouteLinks.ForgotPassword} label="Forgot password?" />
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </AuthTemplate>
  );
};
export { LoginPage };
